import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import Axios from 'axios';
import { Store } from '../Store';
import { api } from '../Api';
import '../assets/wallet.css';

// How long a revealed balance stays on screen before it masks itself again.
const AUTO_HIDE_MS = 10000;
const COUNT_UP_MS = 700;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Floating-point sums come back as 47.60000000000001 — show money-style.
const fmt = (v) =>
  num(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const sumAmounts = (rows, pick) =>
  (Array.isArray(rows) ? rows : []).filter(pick).reduce((a, r) => a + num(r.amount), 0);

const NO_TOTALS = {
  reference: 0,
  groupSales: 0,
  profitIncentive: 0,
  incomeGeneration: 0,
  shoppingBalance: 0,
  shoppingPoint: 0,
  psi: 0,
  // Amount each source has converted into Available Balance, keyed by the
  // convert source (matching, global, referencepoint, ...).
  converted: {},
  convertedTotal: 0,
  convertedNetTotal: 0,
};

export default function MyWalletScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();
  const auth = { headers: { authorization: `Bearer ${userInfo.token}` } };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [w, setW] = useState({
    available: 0,
    shoppingBalance: 0,
    shoppingPoint: 0,
    registerPoint: 0,
    psi: 0,
    reference: 0,
    groupSales: 0,
    pi: 0,
    ig: 0,
  });
  // Lifetime amount each entry has brought in, from its own history.
  const [totals, setTotals] = useState(NO_TOTALS);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [point, totalsRes, group, global] = await Promise.all([
        Axios.get(api + `/api/users/point/${userInfo._id}`, auth),
        Axios.get(api + '/api/users/wallet/totals', auth).catch(() => ({ data: NO_TOTALS })),
        Axios.get(api + '/api/availablebalance/getgroupsales', auth).catch(() => ({ data: {} })),
        Axios.post(api + '/api/costs/myglobal', { username: userInfo.username }, auth).catch(
          () => ({ data: [] })
        ),
      ]);
      const p = point.data || {};
      const g = group.data?.totals || {};
      setW({
        available: num(p.availablebalance),
        shoppingBalance: num(p.shoppingbalance),
        shoppingPoint: num(p.shoppingpoint),
        registerPoint: num(p.point),
        psi: num(p.psipoint),
        // Reference Bonus waiting to be converted into Available Balance.
        reference: num(p.referencePoints),
        groupSales: num(g.Pending),
        pi: sumAmounts(global.data, (r) => r.status === 'Pending'),
        ig: num(p.incomeGeneration),
      });
      setTotals({ ...NO_TOTALS, ...(totalsRes.data || {}) });
      setUpdatedAt(new Date());
    } catch (err) {
      setError('We could not load your wallet. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo._id, userInfo.token, userInfo.username]);

  useEffect(() => {
    load();
  }, [load]);

  // ---- masked balance --------------------------------------------------
  const [revealed, setRevealed] = useState(false);
  const [shown, setShown] = useState(0);
  const [shownReg, setShownReg] = useState(0);
  const hideTimer = useRef(null);
  const frame = useRef(null);

  const stopTimers = () => {
    clearTimeout(hideTimer.current);
    cancelAnimationFrame(frame.current);
  };

  const toggleReveal = () => {
    stopTimers();
    if (revealed) {
      setRevealed(false);
      return;
    }
    setRevealed(true);
    const target = w.available;
    const targetReg = w.registerPoint;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / COUNT_UP_MS, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(target * eased);
      setShownReg(targetReg * eased);
      if (t < 1) frame.current = requestAnimationFrame(tick);
      else {
        setShown(target);
        setShownReg(targetReg);
      }
    };
    frame.current = requestAnimationFrame(tick);
    hideTimer.current = setTimeout(() => setRevealed(false), AUTO_HIDE_MS);
  };

  useEffect(() => stopTimers, []);

  // ---- breakdown -------------------------------------------------------
  // `withdraw` is what that source has converted into Available Balance —
  // the only way Available Balance grows. Sources with no path into it
  // (Shopping Point, Shopping Balance, Reward, Hub Maintenance) show a dash.
  const conv = (from) => num(totals.converted?.[from]);
  const rows = [
    { key: 'ref', icon: 'fa-handshake-o', label: 'Reference', income: w.reference, total: totals.reference, withdraw: conv('referencepoint') },
    { key: 'grp', icon: 'fa-users', label: 'Group Sales', income: w.groupSales, total: totals.groupSales, withdraw: conv('matching') },
    { key: 'reg', icon: 'fa-user-plus', label: 'Register Point', income: w.registerPoint, withdraw: conv('point') },
    { key: 'sp', icon: 'fa-shopping-bag', label: 'Shopping Point', income: w.shoppingPoint, total: totals.shoppingPoint },
    { key: 'rw', icon: 'fa-trophy', label: 'Reward' },
    { key: 'sb', icon: 'fa-credit-card', label: 'Shopping Balance', income: w.shoppingBalance, total: totals.shoppingBalance, to: '/shopping-balance-log', toLabel: 'View full log' },
    { key: 'hub', icon: 'fa-building-o', label: 'Hub Maintenance' },
    { key: 'pi', icon: 'fa-line-chart', label: 'Profit Incentive (PI)', income: w.pi, total: totals.profitIncentive, withdraw: conv('global') },
    { key: 'psi', icon: 'fa-pie-chart', label: 'Profit Sharing Incentive (PSI)', income: w.psi, total: totals.psi, withdraw: conv('psipoint') },
    { key: 'ig', icon: 'fa-sitemap', label: 'Income Generation', income: w.ig, total: totals.incomeGeneration, withdraw: conv('incomegeneration') },
  ];
  const convertedTotal = num(totals.convertedTotal);
  const convertedNet = num(totals.convertedNetTotal);

  const cell = (v) => (v === undefined ? <span className="wl-dash">—</span> : fmt(v));

  return (
    <div className="wl-page">
      <Helmet>
        <title>My Wallet</title>
      </Helmet>

      <div className="wl-wrap">
        <header className="wl-top">
          <button className="wl-back" onClick={() => navigate('/transection')} aria-label="Back">
            <i className="fa fa-long-arrow-left" aria-hidden="true"></i>
          </button>
          <div className="wl-top-title">
            <h1>My Wallet</h1>
            <span>{userInfo.name || userInfo.username}</span>
          </div>
          <button
            className={`wl-refresh ${loading ? 'is-spinning' : ''}`}
            onClick={load}
            disabled={loading}
            aria-label="Refresh"
          >
            <i className="fa fa-refresh" aria-hidden="true"></i>
          </button>
        </header>

        <section
          className={`wl-hero ${revealed ? 'is-revealed' : ''}`}
          onClick={toggleReveal}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleReveal()}
          aria-label={revealed ? 'Hide balance' : 'Show balance'}
        >
          <div className="wl-hero-glow" aria-hidden="true" />
          <div className="wl-hero-row">
            <span className="wl-chip">
              <i className="fa fa-shield" aria-hidden="true"></i> Secured
            </span>
            <span className="wl-brand">icon</span>
          </div>

          <div className="wl-balances">
            <div className="wl-bal-main">
              <p className="wl-hero-label">Available Balance</p>
              <h2 className="wl-balance">
                <span className={`wl-mask ${revealed ? 'is-hidden' : ''}`} aria-hidden={revealed}>
                  ****
                </span>
                <span className={`wl-real ${revealed ? 'is-shown' : ''}`} aria-hidden={!revealed}>
                  {fmt(Math.round(shown * 100) / 100)}
                  <small> Point</small>
                </span>
              </h2>
            </div>
            {/* Register Point sits beside it: Sales Generation and
                Shopping Point conversions all land here. */}
            <div className="wl-bal-side">
              <p className="wl-hero-label">
                <i className="fa fa-user-plus" aria-hidden="true"></i> Register Point
              </p>
              <h3 className="wl-balance wl-balance-sm">
                <span className={`wl-mask ${revealed ? 'is-hidden' : ''}`} aria-hidden={revealed}>
                  ****
                </span>
                <span className={`wl-real ${revealed ? 'is-shown' : ''}`} aria-hidden={!revealed}>
                  {fmt(Math.round(shownReg * 100) / 100)}
                </span>
              </h3>
            </div>
          </div>

          <div className="wl-hero-row wl-hero-foot">
            <span className="wl-tap">
              <i className={`fa ${revealed ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true"></i>{' '}
              {revealed ? 'Tap to hide' : 'Tap to reveal'}
            </span>
            <span className="wl-owner">@{userInfo.username}</span>
          </div>
        </section>

        {error && (
          <div className="wl-error">
            <i className="fa fa-exclamation-circle" aria-hidden="true"></i> {error}{' '}
            <button onClick={load}>Retry</button>
          </div>
        )}

        <section className="wl-card">
          <div className="wl-card-head">
            <h3>Earnings breakdown</h3>
            {updatedAt && (
              <span>
                Updated {updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>

          <div className="wl-table" role="table">
            <div className="wl-tr wl-th" role="row">
              <span role="columnheader">Commission Type</span>
              <span role="columnheader">Income Point</span>
              <span role="columnheader">Total</span>
            </div>
            {rows.map((r) => (
              <div className={`wl-tr ${loading ? 'is-loading' : ''}`} role="row" key={r.key}>
                <span className="wl-type" role="cell">
                  <span className="wl-ico">
                    <i className={`fa ${r.icon}`} aria-hidden="true"></i>
                  </span>
                  <span>
                    <b>{r.label}</b>
                    {r.to ? (
                      <em>
                        <Link to={r.to}>{r.toLabel}</Link>
                      </em>
                    ) : null}
                  </span>
                </span>
                <span className="wl-num" role="cell" data-label="Income">
                  {cell(r.income)}
                </span>
                <span className="wl-num wl-muted" role="cell" data-label="Total">
                  {cell(r.total)}
                </span>
                
              </div>
            ))}
            <div className={`wl-tr wl-total ${loading ? 'is-loading' : ''}`} role="row">
              <span className="wl-type" role="cell">
                <span>
                  <b>Total converted to Available Balance</b>
                  {convertedNet !== convertedTotal && (
                    <em className="wl-total-note">{fmt(convertedNet)} credited after convert charges</em>
                  )}
                </span>
              </span>
              <span className="wl-num wl-total-gap" role="cell" />
              <span className="wl-num wl-total-gap" role="cell" />
              <span className="wl-num" role="cell" data-label="Withdraw">
                {fmt(convertedTotal)}
              </span>
            </div>
          </div>
        </section>

        <p className="wl-foot">
          <i className="fa fa-lock" aria-hidden="true"></i> Your balance is hidden by default and hides
          again after {AUTO_HIDE_MS / 1000} seconds.
        </p>
      </div>
    </div>
  );
}
